import { Error as ErrorIcon, Shortcut } from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Chip,
    Link as MuiLink,
    Skeleton,
    Stack,
    Typography,
} from '@mui/material';
import { AccordionProps } from '@mui/material/Accordion/Accordion';
import { useQuery } from '@tanstack/react-query';
import * as React from 'react';
import { useMemo } from 'react';
import { FormattedMessage, MessageDescriptor, useIntl } from 'react-intl';
import { getDocumentInformation } from '../../lib/information/document';
import { EntityData } from '../../lib/information/types/EntityData';
import { EntityType } from '../../lib/information/types/EntityType';
import { getDocumentIcon } from '../../lib/records/documents/document';
import { DocumentStatus } from '../../lib/status';
import { useApplicationContext } from '../application/ApplicationContextProvider';
import { ImpactLink } from '../ImpactLink';
import { StatusChip } from '../StatusChip';
import { DocumentDetails } from './DocumentDetails';

type LinkedDocumentCommon = { label?: MessageDescriptor['id'] };

export type LinkedDocumentDataAsync =
    | (EntityData & { async?: undefined } & LinkedDocumentCommon)
    | ({
          async: true;
          query: () => Promise<EntityData>;
          queryKey: (string | number)[];
          errorLabelKey: MessageDescriptor['id'];
      } & LinkedDocumentCommon);

export type LinkedDocumentDataAsyncProps = LinkedDocumentDataAsync & {
    defaultExpanded?: boolean;
};

interface LinkedDocumentsContentProps {
    documents: LinkedDocumentDataAsyncProps[];
    parent?: EntityType;
}

interface LinkedDocumentAccordionSummaryProps {
    icon: React.ReactNode;
    to: string | null;
    toExternal?: boolean;
    name: string;
    title: string | null;
    status: DocumentStatus | null;
    label?: MessageDescriptor['id'];
    extraLinkTo?: string | null;
    extraLinkName?: string | null;
    parent?: EntityType;
}

const LinkedDocumentAccordionSummary: React.FC<LinkedDocumentAccordionSummaryProps> = ({
    icon,
    to,
    toExternal,
    name,
    title,
    status,
    label,
    extraLinkTo,
    extraLinkName,
    parent,
}) => (
    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="column" spacing={1} width="100%" sx={{ mr: 1 }}>
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                justifyContent="space-between"
                alignItems={{ sm: 'center' }}
                width="100%"
            >
                <Stack direction="row" alignItems="center" spacing={1}>
                    {icon}
                    {to !== null ? (
                        !toExternal ? (
                            <ImpactLink to={to} onClick={(e: React.SyntheticEvent) => e.stopPropagation()}>
                                {name}
                            </ImpactLink>
                        ) : (
                            <MuiLink
                                href={to}
                                target="_blank"
                                onClick={(e: React.SyntheticEvent) => e.stopPropagation()}
                            >
                                {name}
                            </MuiLink>
                        )
                    ) : (
                        <Box>{name}</Box>
                    )}
                    {!!label && <Chip label={<FormattedMessage id={label} />} icon={<Shortcut />} size="small" />}
                </Stack>
                {!!status && (
                    <Box>
                        <StatusChip label={status.label} color={status.color} size="small" />
                    </Box>
                )}
            </Stack>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                    gap: 1,
                    flexDirection: { xs: 'column', sm: 'row' },
                }}
            >
                {!!title && (
                    <Typography variant="body2" color="textSecondary">
                        {title}
                    </Typography>
                )}
                {!!extraLinkTo && !!extraLinkName && parent === EntityType.ActivityNim && (
                    <ImpactLink
                        to={extraLinkTo}
                        variant="body2"
                        sx={{ marginLeft: { sm: '2rem' } }}
                        onClick={(e: React.SyntheticEvent) => e.stopPropagation()}
                    >
                        {extraLinkName}
                    </ImpactLink>
                )}
            </Box>
        </Stack>
    </AccordionSummary>
);

const accordionProps: Omit<AccordionProps, 'children'> = {
    variant: 'outlined',
    sx: {
        '&:before': {
            display: 'none',
        },
    },
};

interface LinkedDocumentAccordionProps {
    document: EntityData;
    defaultExpanded?: boolean;
    label?: MessageDescriptor['id'];
    parent?: EntityType;
}

const LinkedDocumentAccordion: React.FC<LinkedDocumentAccordionProps> = ({
    document,
    defaultExpanded,
    label,
    parent,
}) => {
    const intl = useIntl();
    const { environment } = useApplicationContext();
    const information = useMemo(() => getDocumentInformation(document, environment, intl), [document]);
    const Icon = getDocumentIcon(document.type);
    return (
        <Accordion {...accordionProps} defaultExpanded={defaultExpanded}>
            <LinkedDocumentAccordionSummary
                icon={<Icon color="action" />}
                to={information.url}
                toExternal={information.urlExternal}
                name={information.name}
                title={information.title}
                status={information.status}
                label={label}
                extraLinkTo={information.extraUrl}
                extraLinkName={information.extraUrlName}
                parent={parent}
            />
            <AccordionDetails>
                <DocumentDetails {...document} withType />
            </AccordionDetails>
        </Accordion>
    );
};

interface LinkedDocumentAccordionAsyncProps {
    query: () => Promise<EntityData>;
    queryKey: (string | number)[];
    errorLabelKey: MessageDescriptor['id'];
    defaultExpanded?: boolean;
    parent?: EntityType;
}

const LinkedDocumentAccordionAsync: React.FC<LinkedDocumentAccordionAsyncProps> = ({
    query,
    queryKey,
    errorLabelKey,
    defaultExpanded,
    parent,
}) => {
    const { data, isLoading, isError } = useQuery(queryKey, () => query());
    const [open, setOpen] = React.useState(defaultExpanded);
    return data && !isLoading ? (
        <LinkedDocumentAccordion document={data} defaultExpanded={open} parent={parent} />
    ) : (
        <Accordion {...accordionProps} expanded={open} onChange={() => setOpen(!open)}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                {isError ? (
                    <Stack direction="row" spacing={1} alignItems="center">
                        <ErrorIcon color="error" />
                        <Box>
                            <FormattedMessage id={errorLabelKey} />
                        </Box>
                    </Stack>
                ) : (
                    <Stack direction="row" spacing={1} sx={{ width: '75%' }}>
                        <Skeleton variant="text" sx={{ flexGrow: 4 }} />
                        <Skeleton variant="text" sx={{ flexGrow: 1 }} />
                    </Stack>
                )}
            </AccordionSummary>
        </Accordion>
    );
};

export const LinkedDocumentsContent: React.FC<LinkedDocumentsContentProps> = ({ documents, parent }) => {
    return (
        <>
            {documents.map((doc, i) => {
                // Weird API, but convenient to use
                if ('async' in doc && doc.async) {
                    return (
                        <LinkedDocumentAccordionAsync
                            key={i}
                            query={doc.query}
                            queryKey={doc.queryKey}
                            errorLabelKey={doc.errorLabelKey}
                            defaultExpanded={doc.defaultExpanded}
                        />
                    );
                } else {
                    return (
                        <LinkedDocumentAccordion
                            key={i}
                            document={doc}
                            defaultExpanded={doc.defaultExpanded}
                            label={doc.label}
                            parent={parent}
                        />
                    );
                }
            })}
        </>
    );
};
